import { useState } from 'react';
import type { Aporte } from '../../lib/sesion';
import type { PropsVista } from './registro';
import { useMediaUrl } from '../../lib/media';
import { Ampliada } from './Ampliada';

/**
 * Vista `galeria`: galería anónima de aportes para la Pantalla (legible a
 * 6 m). Tarjetas con lo escrito por las mesas — y, si el aporte es una FOTO
 * (moodboard, mural), la imagen misma con su ficha: la evidencia visual se
 * proyecta como tal, no como un renglón de texto. SIN autor jamás (C7); el
 * grupo sí puede verse porque es agregable. Respeta `opciones` del
 * manifiesto: `titulo`, `columnas`, `campo_texto` (clave del payload donde
 * vive el texto — o LISTA de claves: la tarjeta muestra cada campo presente
 * con su nombre capitalizado como microetiqueta, en orden, p. ej. «Espero: …»
 * / «Temo: …»), `campo_etiqueta` (clave con la etiqueta de la tarjeta, p. ej.
 * el cuadrante del mapa de empatía) y `ordenar_por` (clave numérica de orden,
 * p. ej. el paso del storyboard). `anonimo` no se lee: el anonimato es la
 * regla, no una opción.
 */

/** Tarjetas máximas en proyección; el resto se resume en "y N más". */
const MAX_TARJETAS = 12;

/** Claves del payload que son metadatos del objeto/servidor, nunca ficha. */
const CLAVES_META = new Set([
  'idem',
  'mime',
  'size',
  'filename',
  'media_key',
  'seccion',
  'sobre',
  'microfono_sala',
  'inicio_grabacion',
  'num_hablantes',
  'curaduria',
  'accion',
]);

/** Extrae el texto del aporte en la clave declarada, o null si no sirve. */
function textoDe(aporte: Aporte, clave: string): string | null {
  const v = (aporte.payload ?? {})[clave];
  if (typeof v !== 'string') return null;
  const limpio = v.trim();
  return limpio.length > 0 ? limpio : null;
}

/**
 * Ficha de una foto sin `campo_texto` declarado: todos los campos string del
 * payload que no sean metadatos, en el orden en que el formato los mandó
 * (titulo primero, por_que después).
 */
function fichaDe(aporte: Aporte): { campo: string; texto: string }[] {
  return Object.entries(aporte.payload ?? {})
    .filter(([k, v]) => !CLAVES_META.has(k) && typeof v === 'string' && v.trim().length > 0)
    .map(([campo, v]) => ({ campo, texto: (v as string).trim() }));
}

/** La imagen de un aporte foto, descargada autenticada (object URL). */
function ImagenMedia({ aporteId, alta = false }: { aporteId: string; alta?: boolean }) {
  const { url, cargando } = useMediaUrl(aporteId);
  if (cargando) {
    return (
      <div
        className={`w-full animate-pulse rounded-md bg-surface-sunken ${alta ? 'h-72' : 'h-36'}`}
        aria-hidden="true"
      />
    );
  }
  if (!url) return null; // si el objeto no carga, la ficha igual se lee
  return (
    <img
      src={url}
      alt=""
      className={`w-full rounded-md ${alta ? 'max-h-[55vh] object-contain' : 'h-36 object-cover'}`}
    />
  );
}

/** Nombre de campo capitalizado para la microetiqueta («espero» → «Espero»). */
function capitalizar(nombre: string): string {
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

export function VistaGaleria({ aportes, opciones }: PropsVista) {
  const titulo = typeof opciones.titulo === 'string' ? opciones.titulo : 'Lo que dicen las mesas';
  const columnas =
    typeof opciones.columnas === 'number' && opciones.columnas >= 1
      ? Math.floor(opciones.columnas)
      : 3;
  // `campo_texto` acepta una clave (string) o una LISTA de claves: con lista,
  // cada campo presente se muestra con su nombre como microetiqueta, en orden.
  const listaCampos = Array.isArray(opciones.campo_texto)
    ? opciones.campo_texto.filter((c): c is string => typeof c === 'string')
    : null;
  const conMicroetiqueta = listaCampos != null && listaCampos.length > 0;
  const camposTexto = conMicroetiqueta
    ? listaCampos
    : [typeof opciones.campo_texto === 'string' ? opciones.campo_texto : 'texto'];
  const campoEtiqueta = typeof opciones.campo_etiqueta === 'string' ? opciones.campo_etiqueta : null;
  const ordenarPor = typeof opciones.ordenar_por === 'string' ? opciones.ordenar_por : null;

  // Aportes con texto en alguna clave declarada, más las FOTOS (imagen +
  // ficha aunque el manifiesto no declare campo_texto); los demás se saltan.
  const conTexto = aportes
    .map((a) => {
      const esFoto = a.tipo === 'foto';
      let partes = camposTexto
        .map((campo) => {
          const texto = textoDe(a, campo);
          return texto == null ? null : { campo, texto };
        })
        .filter((parte): parte is NonNullable<typeof parte> => parte != null);
      if (partes.length === 0 && esFoto) partes = fichaDe(a);
      if (partes.length === 0 && !esFoto) return null;
      const p = a.payload ?? {};
      const etiqueta = campoEtiqueta != null ? p[campoEtiqueta] : null;
      const orden = ordenarPor != null ? p[ordenarPor] : null;
      return {
        id: a.id,
        partes,
        fotoId: esFoto ? a.id : null,
        grupo: a.grupo_nombre,
        creado: a.created_at,
        etiqueta: typeof etiqueta === 'string' && etiqueta.trim() ? etiqueta.trim() : null,
        orden: typeof orden === 'number' ? orden : null,
      };
    })
    .filter((t): t is NonNullable<typeof t> => t != null);
  if (conTexto.length === 0) return null;

  // Con `ordenar_por` (storyboard: el paso) manda ese número ascendente; si no,
  // los más recientes primero. Si desbordan, se muestran y queda un contador.
  if (ordenarPor != null) {
    conTexto.sort((a, b) => (a.orden ?? Infinity) - (b.orden ?? Infinity));
  } else {
    conTexto.sort((a, b) => b.creado.localeCompare(a.creado));
  }
  const visibles = conTexto.slice(0, MAX_TARJETAS);
  const restantes = conTexto.length - visibles.length;

  return (
    <GaleriaConZoom
      titulo={titulo}
      columnas={columnas}
      visibles={visibles}
      restantes={restantes}
      conMicroetiqueta={conMicroetiqueta}
    />
  );
}

type Tarjeta = {
  id: string;
  partes: { campo: string; texto: string }[];
  /** id del aporte cuando es FOTO: la tarjeta dibuja la imagen. */
  fotoId: string | null;
  grupo: string | null;
  creado: string;
  etiqueta: string | null;
  orden: number | null;
};

/** Galería con zoom: clic en una tarjeta la agranda a sala entera (Ampliada). */
function GaleriaConZoom({
  titulo,
  columnas,
  visibles,
  restantes,
  conMicroetiqueta,
}: {
  titulo: string;
  columnas: number;
  visibles: Tarjeta[];
  restantes: number;
  conMicroetiqueta: boolean;
}) {
  const [ampliada, setAmpliada] = useState<Tarjeta | null>(null);

  return (
    <section className="mt-10 w-full max-w-6xl text-left">
      <h2 className="mb-6 text-center font-sans text-lg uppercase tracking-wide text-content-muted">
        {titulo}
      </h2>
      <ul
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}
      >
        {visibles.map((t) => (
          <li key={t.id}>
            {/* Interactiva (pedido del owner): clic → se lee a sala entera. */}
            <button
              type="button"
              onClick={() => setAmpliada(t)}
              aria-label="Ampliar este aporte"
              className="flex h-full w-full flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5 text-left transition-colors duration-fast hover:border-cyan focus-visible:outline-none focus-visible:shadow-focus"
            >
              {(t.etiqueta != null || t.orden != null) && (
                <p className="font-sans text-base font-semibold uppercase tracking-wide text-cyan">
                  {t.orden != null ? `Paso ${t.orden}` : t.etiqueta}
                </p>
              )}
              {t.fotoId != null && <ImagenMedia aporteId={t.fotoId} />}
              {conMicroetiqueta ? (
                <div className="flex flex-col gap-2">
                  {t.partes.map((parte) => (
                    <p key={parte.campo} className="font-sans text-2xl leading-snug text-content">
                      <span className="mr-2 font-sans text-base font-semibold uppercase tracking-wide text-content-muted">
                        {capitalizar(parte.campo)}:
                      </span>
                      {parte.texto}
                    </p>
                  ))}
                </div>
              ) : t.fotoId != null ? (
                /* Ficha de la foto: el título al frente, el porqué debajo. */
                <div className="flex flex-col gap-1">
                  {t.partes[0] && (
                    <p className="font-sans text-xl font-semibold leading-snug text-content">
                      {t.partes[0].texto}
                    </p>
                  )}
                  {t.partes.slice(1).map((parte) => (
                    <p key={parte.campo} className="font-sans text-lg leading-snug text-content-muted">
                      {parte.texto}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="font-sans text-2xl leading-snug text-content">{t.partes[0].texto}</p>
              )}
              {t.grupo != null && (
                <p className="mt-auto font-sans text-base uppercase tracking-wide text-content-muted">
                  {t.grupo}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>
      {restantes > 0 && (
        <p className="mt-4 text-center font-sans text-lg text-content-muted">
          y <span className="font-semibold tabular-nums text-content">{restantes}</span> más
        </p>
      )}

      <Ampliada abierta={ampliada != null} onCerrar={() => setAmpliada(null)}>
        {ampliada && (
          <div className="flex flex-col gap-6">
            {(ampliada.etiqueta != null || ampliada.orden != null) && (
              <p className="font-sans text-2xl font-semibold uppercase tracking-wide text-cyan">
                {ampliada.orden != null ? `Paso ${ampliada.orden}` : ampliada.etiqueta}
              </p>
            )}
            {ampliada.fotoId != null && <ImagenMedia aporteId={ampliada.fotoId} alta />}
            {ampliada.partes.map((parte, i) => (
              <p
                key={parte.campo}
                className={
                  ampliada.fotoId != null && i > 0
                    ? 'font-sans text-2xl leading-snug text-content-muted'
                    : 'font-display text-4xl font-semibold leading-tight text-content'
                }
              >
                {conMicroetiqueta && (
                  <span className="mr-3 font-sans text-2xl font-semibold uppercase tracking-wide text-content-muted">
                    {capitalizar(parte.campo)}:
                  </span>
                )}
                {parte.texto}
              </p>
            ))}
            {ampliada.grupo != null && (
              <p className="font-sans text-xl uppercase tracking-wide text-content-muted">
                {ampliada.grupo}
              </p>
            )}
          </div>
        )}
      </Ampliada>
    </section>
  );
}
